/**
 * Safe display text for API values that may be strings or nested masters ({ id, name }).
 * Never return a plain object (avoids "Objects are not valid as a React child").
 */
export function displayLabel(value, fallback = "—") {
  if (value == null || value === "") return fallback;
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  if (Array.isArray(value)) {
    const parts = value.map((v) => displayLabel(v, "")).filter(Boolean);
    return parts.length ? parts.join(", ") : fallback;
  }
  if (typeof value === "object") {
    const n =
      value.name ??
      value.label ??
      value.title ??
      value.code ??
      value.empCode ??
      value.employeeCode ??
      value.fullName ??
      null;
    if (n != null && typeof n !== "object") return String(n);
    if (n != null && typeof n === "object") return displayLabel(n, fallback);
    if (value.user) {
      const un = [value.user.name, value.user.lastName].filter(Boolean).join(" ").trim();
      if (un) return un;
    }
  }
  return fallback;
}

const MASTER_KEYS = [
  "department",
  "designation",
  "branch",
  "subDepartment",
  "grade",
  "category",
  "location",
  "reportingManager",
];

/**
 * Flatten nested Sequelize masters ({ id, name }) to display strings on employee-like rows.
 * Keeps *Id fields (fills from nested .id when missing). Safe for React children.
 */
export function flattenEmployeeMasters(row) {
  if (!row || typeof row !== "object" || Array.isArray(row)) return row;
  const out = { ...row };
  for (const key of MASTER_KEYS) {
    const val = out[key];
    if (val == null || typeof val !== "object") continue;
    const idKey = `${key}Id`;
    if ((out[idKey] == null || out[idKey] === "") && val.id != null) {
      out[idKey] = val.id;
    }
    const nameKey = key === "subDepartment" ? "subDepartmentName" : `${key}Name`;
    const label = displayLabel(val, "");
    if (out[nameKey] == null || out[nameKey] === "") out[nameKey] = label;
    out[key] = label;
  }
  if (out.name != null && typeof out.name === "object") {
    out.name = displayLabel(out.name, "");
  } else if (!out.name && out.user) {
    const n = [out.user.name, out.user.lastName].filter(Boolean).join(" ").trim();
    if (n) out.name = n;
  }
  if (out.fullName != null && typeof out.fullName === "object") {
    out.fullName = displayLabel(out.fullName, "");
  }
  if (out.status != null && typeof out.status === "object") {
    out.status = displayLabel(out.status, "");
  }
  if (out.employmentStatus != null && typeof out.employmentStatus === "object") {
    out.employmentStatus = displayLabel(out.employmentStatus, "");
  }
  return out;
}

export function flattenEmployeeList(list) {
  if (!Array.isArray(list)) return list;
  return list.map(flattenEmployeeMasters);
}

/** Normalize typical API envelopes that wrap employee rows in .data */
export function flattenEmployeesInResponse(payload) {
  if (!payload || typeof payload !== "object") return payload;
  if (Array.isArray(payload)) return flattenEmployeeList(payload);
  if (Array.isArray(payload.data)) {
    return { ...payload, data: flattenEmployeeList(payload.data) };
  }
  if (payload.data && typeof payload.data === "object" && !Array.isArray(payload.data)) {
    return { ...payload, data: flattenEmployeeMasters(payload.data) };
  }
  return flattenEmployeeMasters(payload);
}

export function employeeDisplayName(emp) {
  if (!emp) return "—";
  const fromUser = [emp.user?.name, emp.user?.lastName].filter(Boolean).join(" ").trim();
  if (fromUser) return fromUser;
  return displayLabel(
    emp.name || emp.fullName || [emp.firstName, emp.lastName].filter(Boolean).join(" ").trim(),
    "—"
  );
}

export function employeeDesignation(emp) {
  return displayLabel(emp?.designationName || emp?.designation);
}

export function employeeDepartment(emp) {
  return displayLabel(emp?.departmentName || emp?.department);
}
